import { useEffect, useState } from "react";
import {
  getResidentReportOrganizationOptions,
  getResidentReportUserOptions,
} from "../api/residentReportOptions.api";
import type {
  ResidentReportFilterOption,
  ResidentReportOrganizationOption,
} from "../types/resident-report-filter.types";

export function useResidentReportFilterOptions(
  organizationId: string,
  { loadUsers = true }: { loadUsers?: boolean } = {},
) {
  const [organizationOptions, setOrganizationOptions] = useState<
    ResidentReportOrganizationOption[]
  >([]);
  const [userOptions, setUserOptions] = useState<ResidentReportFilterOption[]>([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [organizationError, setOrganizationError] = useState<unknown>(null);
  const [userError, setUserError] = useState<unknown>(null);

  const populationOptions =
    organizationOptions.find((option) => option.id === organizationId)?.neighborhoods ?? [];

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingOrganizations(true);
    setOrganizationError(null);

    void getResidentReportOrganizationOptions(controller.signal)
      .then(setOrganizationOptions)
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setOrganizationError(error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingOrganizations(false);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!organizationId || !loadUsers) {
      setUserOptions([]);
      setUserError(null);
      return;
    }

    const controller = new AbortController();
    setIsLoadingUsers(true);
    setUserError(null);

    void getResidentReportUserOptions({ signal: controller.signal })
      .then(setUserOptions)
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setUserError(error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoadingUsers(false);
      });

    return () => controller.abort();
  }, [organizationId, loadUsers]);

  return {
    organizationOptions,
    populationOptions,
    userOptions,
    isLoadingOrganizations,
    isLoadingUsers,
    organizationError,
    userError,
  };
}
